import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRightIcon } from 'lucide-react';
import { ActivityContext } from '../components/ActivityContext';
import { FilterChip } from '../components/FilterChip';
import { PageHeader } from '../components/PageHeader';
import { PersonRow } from '../components/PersonRow';
import { SearchField } from '../components/SearchField';
import { Card, SectionTitle } from '../components/ui/Card';
import { EmptyState } from '../components/ui/EmptyState';
import { people } from '../data/people';
import { categories, topics } from '../data/topics';
import { useLocale } from '../contexts/LocaleContext';
import { useSocial } from '../contexts/SocialContext';
import type { SportCategory } from '../types';

/** `embedded` renders the discovery content inside Community → Discover. */
export function Explore({
  embedded = false
}: {
  embedded?: boolean;
}) {
  const {
    t,
    tl
  } = useLocale();
  const navigate = useNavigate();
  const social = useSocial();
  const [category, setCategory] = useState<SportCategory>('all');
  const matches = (value: SportCategory) => category === 'all' || value === category;
  const visiblePosts = social.posts.filter(post => matches(post.context.category));
  const visibleTopics = topics.filter(topic => matches(topic.category));
  const visiblePeople = people.filter(person => person.id !== 'me' && matches(person.category));
  const featured = visiblePosts[0];
  const trending = visiblePosts.slice(featured ? 1 : 0);
  const nothing = !featured && visibleTopics.length === 0 && visiblePeople.length === 0;
  return <div className={embedded ? '' : 'px-2 pb-2'} data-id="element-3015" data-mp="3v4bfcle8bwj6">
      {!embedded && <PageHeader title={t('explore.title')} subtitle={t('explore.subtitle')} showBack data-id="element-3016" data-mp="3q69txttbzl4j" />}

      <SearchField value="" onChange={() => undefined} readOnly onActivate={() => navigate('/busca')} data-id="element-3017" data-mp="1tdhtjgdnej2q" />

      <ul className="bee-rail -mx-2 mt-2 flex gap-1 overflow-x-auto px-2 pb-1" data-id="element-3018" data-mp="1ofyewfd1g4a9">
        {categories.map(item => <li key={item} className="shrink-0" data-id="element-3019" data-mp="3itkf4wxezdmv">
            <FilterChip label={t(`category.${item}`)} active={category === item} onClick={() => setCategory(item)} data-id="element-3020" data-mp="3nm3sptw3q8ro" />
          </li>)}
      </ul>

      {nothing ? <div className="mt-3" data-id="element-3021" data-mp="3dwk7ihkx22tn">
          <EmptyState title={t('explore.empty.title')} body={t('explore.empty.body')} data-id="element-3022" data-mp="3dtppsxtaew3x" />
        </div> : <>
          {featured && <section className="mt-3" data-id="element-3023" data-mp="ae0p06s1ibc1">
              <SectionTitle data-id="element-3024" data-mp="aczcryzafisq">{t('explore.featured')}</SectionTitle>
              <button type="button" onClick={() => navigate(`/post/${featured.id}`)} className="block w-full overflow-hidden rounded-panel text-left transition-transform duration-150 ease-brand active:scale-[0.99]" data-id="element-3025" data-mp="3sjn7qw0mipko">
                <div className="relative" data-id="element-3026" data-mp="2l7mscnc04m54">
                  <img src={featured.media} alt="" aria-hidden="true" className="aspect-[16/9] w-full object-cover" data-id="element-3027" data-mp="2l8g1tw2chpqy" />
                  <span className="absolute inset-0 bg-deep/45" aria-hidden="true" data-id="element-3028" data-mp="28qvg9lg2n4gi" />
                  <div className="absolute inset-0 flex flex-col justify-end p-2" data-id="element-3029" data-mp="2nl0a20mf0aqj">
                    <ActivityContext context={featured.context} tone="dark" data-id="element-3030" data-mp="2in89ki7pfgaq" />
                    <p className="mt-1 text-[15px] font-bold leading-snug text-sand" data-id="element-3031" data-mp="1uy5thespxvic">
                      {tl(featured.caption)}
                    </p>
                  </div>
                </div>
              </button>
            </section>}

          {visibleTopics.length > 0 && <section className="mt-3" data-id="element-3032" data-mp="1zvxncecxbrp6">
              <SectionTitle data-id="element-3033" data-mp="1zu4c0lrd8jau">{t('explore.topics')}</SectionTitle>
              <ul className="rounded-card border border-sand-line bg-white/70 px-2" data-id="element-3034" data-mp="1c5l4w9k1yckc">
                {visibleTopics.map(topic => <li key={topic.id} className="border-b border-sand-line last:border-b-0" data-id="element-3035" data-mp="175fkneg43sca">
                    <button type="button" onClick={() => navigate('/busca')} className="flex w-full items-center gap-2 py-2 text-left" data-id="element-3036" data-mp="ahzlxy82voxt">
                      <span className="min-w-0 flex-1" data-id="element-3037" data-mp="o7jj8oiw3tzq">
                        <span className="block text-[13.5px] font-medium text-deep" data-id="element-3038" data-mp="o8bdnw0r3lof">
                          {tl(topic.title)}
                        </span>
                        <span className="mt-[2px] block text-[11.5px] leading-snug text-muted" data-id="element-3039" data-mp="y57rmh9f500d">
                          {tl(topic.subtitle)}
                        </span>
                        <span className="bee-num mt-1 block text-[10px] font-bold text-gold-deep" data-id="element-3040" data-mp="125hxltx7nqf8">
                          {t('explore.topicPosts', {
                    n: topic.posts
                  })}
                        </span>
                      </span>
                      <ChevronRightIcon size={17} className="shrink-0 text-muted" aria-hidden="true" data-id="element-3041" data-mp="38x8y31jlffsf" />
                    </button>
                  </li>)}
              </ul>
            </section>}

          {visiblePeople.length > 0 && <section className="mt-3" data-id="element-3042" data-mp="2rmdijqdlw87h">
              <SectionTitle data-id="element-3043" data-mp="2mpvd0oagc5u3">{t('explore.people')}</SectionTitle>
              <ul className="rounded-card border border-sand-line bg-white/70 px-2" data-id="element-3044" data-mp="2mnimhmqmon5f">
                {visiblePeople.map(person => <PersonRow key={person.id} person={person} data-id="element-3045" data-mp="3fdr8xqs75tpt" />)}
              </ul>
            </section>}

          {trending.length > 0 && <section className="mt-3" data-id="element-3046" data-mp="36dcm2mzl46k7">
              <SectionTitle data-id="element-3047" data-mp="36cay5hxgzpl2">{t('explore.trending')}</SectionTitle>
              <Card className="px-2" data-id="element-3048" data-mp="1yzsm4bi9x5x2">
                <ul data-id="element-3049" data-mp="1z0u46zxpisoa">
                  {trending.map(post => <li key={post.id} className="border-b border-sand-line last:border-b-0" data-id="element-3050" data-mp="1uyflqgxy9uxj">
                      <button type="button" onClick={() => navigate(`/post/${post.id}`)} className="flex w-full items-start gap-2 py-2 text-left" data-id="element-3051" data-mp="1wg4ibbjg0dtp">
                        <img src={post.media} alt="" aria-hidden="true" className="h-[64px] w-[64px] shrink-0 rounded-[10px] object-cover" data-id="element-3052" data-mp="1rfqgwccck24n" />
                        <span className="min-w-0 flex-1" data-id="element-3053" data-mp="1iheivp95i226">
                          <ActivityContext context={post.context} data-id="element-3054" data-mp="13s06s9e6x6j3" />
                          <span className="mt-1 block text-[12.5px] leading-snug text-deep" data-id="element-3055" data-mp="18p8ragmt9mni">
                            {tl(post.caption)}
                          </span>
                        </span>
                      </button>
                    </li>)}
                </ul>
              </Card>
            </section>}
        </>}
    </div>;
}